import React, { type RefObject } from 'react';

import { cssMap } from '@atlaskit/css';
import { Flex, Grid } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useAudioPlayerStore } from '../../../hooks/useAudioPlayerStore';
import { useAudioPlayerStreamingStore } from '../../../hooks/useAudioPlayerStreamingStore';
import { AudioStreamingState } from '../../../state/AudioPlayerStreamingStore';

import { PlaybackSpeedControl } from './PlaybackSpeedControl';
import { PlayFromBeginningButton } from './PlayFromBeginningButton';
import { PlayPauseButton } from './PlayPauseButton';
import { SkipBackButton } from './SkipBackButton';
import { SkipForwardButton } from './SkipForwardButton';

const gridStyles = cssMap({
	root: {
		gridTemplateColumns: '1fr auto 1fr',
		paddingTop: token('space.050'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.150'),
	},
});

export type MiniplayerControlsProps = {
	audioRef: RefObject<HTMLAudioElement>;
};

export const MiniplayerControls = ({ audioRef }: MiniplayerControlsProps): React.JSX.Element => {
	const { state: streamingState } = useAudioPlayerStreamingStore();
	const isLoading = streamingState.state === AudioStreamingState.LOADING;
	const isUninitialized = streamingState.state === AudioStreamingState.UNINITIALIZED;

	const { pendingContent } = useAudioPlayerStore();

	return (
		<Grid testId="miniplayer-controls" gap="space.300" xcss={gridStyles.root} alignItems="center">
			<Flex justifyContent="end" direction="row" gap="space.200">
				{!pendingContent && !isUninitialized && (
					<>
						<PlayFromBeginningButton isDisabled={isLoading} audioRef={audioRef} />
						<SkipBackButton isDisabled={isLoading} audioRef={audioRef} />
					</>
				)}
			</Flex>
			<PlayPauseButton audioRef={audioRef} />
			<Flex direction="row" gap="space.200">
				{!pendingContent && !isUninitialized && (
					<>
						<SkipForwardButton isDisabled={isLoading} audioRef={audioRef} />
						<PlaybackSpeedControl isDisabled={isLoading} />
					</>
				)}
			</Flex>
		</Grid>
	);
};
